/**
 * Intake Templates Index
 * 
 * Disease-state specific intake forms for NovaMed
 */

// Template definitions
export { MENS_HEALTH_TEMPLATE, calculateADAMScore, calculateStopBangScore } from './mensHealth';
export { WOMENS_HEALTH_TEMPLATE, calculateSymptomScore } from './womensHealth';
export { METABOLIC_TEMPLATE, checkContraindications, calculateBMI } from './metabolic';
export { PEPTIDE_HEALING_TEMPLATE, checkWADAConcerns, calculateInjurySeverity } from './peptideHealing';

// Types
export type {
  IntakeTemplateType,
  QuestionInputType,
  IntakeQuestion,
  ValidationRule,
  ConditionalRule,
  IntakeSection,
  IntakeTemplate,
  ScoringLogic,
  IntakeResponse,
  IntakeSubmission
} from './types';

// Import templates for registry
import { MENS_HEALTH_TEMPLATE } from './mensHealth';
import { WOMENS_HEALTH_TEMPLATE } from './womensHealth';
import { METABOLIC_TEMPLATE } from './metabolic';
import { PEPTIDE_HEALING_TEMPLATE } from './peptideHealing';
import { IntakeTemplate, IntakeTemplateType } from './types';

/**
 * Registry of all available intake templates
 */
export const INTAKE_TEMPLATES: Record<IntakeTemplateType, IntakeTemplate> = {
  mens_health: MENS_HEALTH_TEMPLATE,
  womens_health: WOMENS_HEALTH_TEMPLATE,
  metabolic: METABOLIC_TEMPLATE,
  peptide_healing: PEPTIDE_HEALING_TEMPLATE,
  targeted: MENS_HEALTH_TEMPLATE // Default fallback
};

/**
 * Get an intake template by type
 */
export function getIntakeTemplate(templateType: IntakeTemplateType): IntakeTemplate {
  return INTAKE_TEMPLATES[templateType] || MENS_HEALTH_TEMPLATE;
}

/**
 * Get all available intake templates
 */
export function getAllIntakeTemplates(): IntakeTemplate[] {
  return [
    MENS_HEALTH_TEMPLATE,
    WOMENS_HEALTH_TEMPLATE,
    METABOLIC_TEMPLATE,
    PEPTIDE_HEALING_TEMPLATE
  ];
}

/**
 * Map therapy category to intake template type
 */
export function therapyCategoryToTemplateType(
  therapyCategory: string,
  gender?: string | number
): IntakeTemplateType {
  const categoryLower = therapyCategory.toLowerCase();
  
  if (categoryLower === 'trt' || categoryLower.includes('testosterone')) {
    return 'mens_health';
  }
  if (categoryLower === 'hrt' || categoryLower.includes('hormone') && categoryLower.includes('women')) {
    return 'womens_health';
  }
  if (categoryLower === 'glp1' || categoryLower.includes('semaglutide') || categoryLower.includes('tirzepatide') || categoryLower.includes('weight')) {
    return 'metabolic';
  }
  if (categoryLower === 'peptide' || categoryLower.includes('bpc') || categoryLower.includes('tb-500') || categoryLower.includes('healing')) {
    return 'peptide_healing';
  }
  
  // Gender-based default
  const genderStr = typeof gender === 'number' ? (gender === 1 ? 'male' : 'female') : gender;
  if (genderStr === 'male') {
    return 'mens_health';
  } else if (genderStr === 'female') {
    return 'womens_health';
  }
  
  return 'mens_health';
}
